<div class="schedule-page">
  <!-- Track Manager Toggle -->
  <div class="track-manager-header">
    <button class="track-toggle-btn" (click)="toggleTrackManager()">
      <i class="fa fa-flag"></i>
      {{ 'manageEvent.schedule.manageTracks' | translate }}
      <span class="track-count" *ngIf="tracks.length > 0">({{ tracks.length }})</span>
      <i class="fa fa-chevron-down" [class.rotated]="showTrackManager"></i>
    </button>
  </div>

  <!-- Track Manager Panel -->
  @if (showTrackManager) {
    <div class="track-manager-panel">
      <div class="track-list">
        @if (tracks.length === 0) {
          <div class="no-tracks">
            <i class="fa fa-flag"></i>
            <span>{{ 'manageEvent.schedule.noTracks' | translate }}</span>
          </div>
        } @else {
          @for (track of tracks; track track.track_id) {
            <div class="track-item">
              @if (editingTrack?.track_id === track.track_id) {
                <div class="track-edit-form">
                  <input type="text"
                         [(ngModel)]="editTrackName"
                         [placeholder]="'manageEvent.schedule.trackNamePlaceholder' | translate"
                         class="track-input">
                  <input type="text"
                         [(ngModel)]="editTrackDescription"
                         [placeholder]="'manageEvent.schedule.descriptionOptional' | translate"
                         class="track-input">
                  <div class="track-edit-actions">
                    <button class="btn-save" (click)="saveTrack()">{{ 'common.save' | translate }}</button>
                    <button class="btn-cancel" (click)="cancelEditTrack()">{{ 'common.cancel' | translate }}</button>
                  </div>
                </div>
              } @else {
                <div class="track-info">
                  <span class="track-name">{{ track.track_name }}</span>
                  @if (track.track_description) {
                    <span class="track-desc">{{ track.track_description }}</span>
                  }
                </div>
                <div class="track-actions">
                  <button class="icon-btn-sm" (click)="startEditTrack(track)" [title]="'common.edit' | translate">
                    <i class="fa fa-pen"></i>
                  </button>
                  <button class="icon-btn-sm danger" (click)="deleteTrack(track)" [title]="'common.delete' | translate">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              }
            </div>
          }
        }
      </div>
      <div class="add-track-form">
        <input type="text"
               [(ngModel)]="newTrackName"
               [placeholder]="'manageEvent.schedule.trackNameExample' | translate"
               class="track-input">
        <input type="text"
               [(ngModel)]="newTrackDescription"
               [placeholder]="'manageEvent.schedule.descriptionOptional' | translate"
               class="track-input">
        <button class="add-track-btn" (click)="addTrack()" [disabled]="!newTrackName.trim()">
          <i class="fa fa-plus"></i> {{ 'manageEvent.schedule.addTrack' | translate }}
        </button>
      </div>
    </div>
  }

  <!-- Day Tabs Navigation -->
  @if (eventDays.length > 0) {
    <div class="day-tabs-wrapper">
      <div class="day-tabs">
        @for (day of eventDays; track day.date.getTime()) {
          <button class="day-tab"
                  [class.active]="selectedDay && isSameDay(selectedDay, day.date)"
                  [class.today]="day.isToday"
                  (click)="selectDay(day)">
            <span class="day-label">{{ day.label }}</span>
            <span class="day-label-short">{{ day.shortLabel }}</span>
            @if (getClassCountForDay(day) > 0) {
              <span class="day-count">{{ getClassCountForDay(day) }}</span>
            }
          </button>
        }
      </div>
    </div>
  }

  @if (loading) {
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{ 'manageEvent.schedule.loading' | translate }}</p>
    </div>
  } @else if (filteredClasses.length === 0) {
    <div class="empty-page">
      <i class="fa fa-calendar-xmark"></i>
      <p>{{ 'manageEvent.schedule.noClassesForDay' | translate }}</p>
    </div>
  } @else {
    <div class="classes-list">
      @for (item of dayTimeline; track $index; let isLast = $last) {
        @if (item.type === 'class' && item.eventClass) {
          <div class="class-section">
            <!-- Class Header Row -->
            <div class="class-row"
                 [class.class-in-progress]="getClassStatus(item.eventClass) === 'in_progress'"
                 [class.class-completed]="getClassStatus(item.eventClass) === 'completed'">
              <div class="class-time">
                <span class="time-badge">{{ getClassStartTime(item.eventClass) }}</span>
              </div>
              <div class="class-info">
                <h4>
                  {{ item.eventClass.class_name }}
                  @if (getClassStatus(item.eventClass) === 'in_progress') {
                    <span class="status-badge-inline in-progress">
                      <span class="pulse-dot"></span>
                      {{ 'manageEvent.schedule.inProgress' | translate }}
                    </span>
                  }
                  @if (getClassStatus(item.eventClass) === 'completed') {
                    <span class="status-badge-inline completed">
                      <i class="fa fa-check-circle"></i>
                      {{ 'manageEvent.schedule.completed' | translate }}
                    </span>
                  }
                </h4>
                <span class="class-stats">
                  {{ item.eventClass.event_class_registrations?.length || 0 }} {{ 'manageEvent.schedule.registered' | translate }}
                  <span class="dot"></span>
                  {{ getSchedulesForClass(item.eventClass.event_class_id).length }} {{ getSchedulesForClass(item.eventClass.event_class_id).length !== 1 ? ('manageEvent.schedule.schedules' | translate) : ('manageEvent.schedule.schedule' | translate) }}
                </span>
              </div>
              <div class="class-actions">
                <!-- Start Button (when pending) -->
                @if (getClassStatus(item.eventClass) === 'pending') {
                  <button
                    class="action-btn start"
                    (click)="startClass(item.eventClass)"
                    [title]="'manageEvent.schedule.startClass' | translate"
                  >
                    <i class="fa fa-play"></i>
                    {{ 'manageEvent.schedule.start' | translate }}
                  </button>
                }

                <!-- Complete Button (when in progress) -->
                @if (getClassStatus(item.eventClass) === 'in_progress') {
                  <button
                    class="action-btn complete"
                    (click)="completeClass(item.eventClass)"
                    [title]="'manageEvent.schedule.completeClass' | translate"
                  >
                    <i class="fa fa-check"></i>
                    {{ 'manageEvent.schedule.complete' | translate }}
                  </button>
                }

                <!-- Completed Badge -->
                @if (getClassStatus(item.eventClass) === 'completed') {
                  <span class="status-badge completed">
                    <i class="fa fa-check-circle"></i>
                    {{ 'manageEvent.schedule.completed' | translate }}
                  </span>
                }

                <!-- Results Button -->
                <button
                  class="action-btn results"
                  (click)="openResultsView(item.eventClass)"
                  [title]="'manageEvent.schedule.viewResults' | translate"
                >
                  <i class="fa fa-trophy"></i>
                  {{ 'manageEvent.schedule.results' | translate }}
                </button>
                <button class="add-btn" (click)="toggleTypeSelector(item.eventClass.event_class_id)">
                  <i class="fa fa-plus"></i>
                  {{ 'manageEvent.schedule.addSchedule' | translate }}
                </button>

                @if (showTypeSelector === item.eventClass.event_class_id) {
                  <div class="type-dropdown">
                    @for (type of scheduleTypes; track type.value) {
                      <div class="type-item" (click)="createScheduleWithType(item.eventClass!, type); $event.stopPropagation()">
                        <div class="type-icon-wrapper" [class]="type.value">
                          <i class="fa {{ type.icon }}"></i>
                        </div>
                        <div class="type-text">
                          <span>{{ type.label }}</span>
                          <small>{{ type.description }}</small>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>

            <!-- Schedules -->
            @if (hasSchedules(item.eventClass.event_class_id)) {
              <div class="schedules">
                @for (schedule of getSchedulesForClass(item.eventClass.event_class_id); track schedule.schedule_id) {
                  <div class="schedule-row">
                    <i class="fa {{ getScheduleTypeIcon(schedule) }} schedule-icon"></i>
                    <div class="schedule-info">
                      <span class="schedule-name">{{ schedule.schedule_name }}</span>
                      <span class="schedule-meta">
                        {{ getEntryCount(schedule) }} {{ schedule.session_type === 'group' ? ('manageEvent.schedule.participants' | translate) : ('manageEvent.schedule.entries' | translate) }}
                        · {{ formatDuration(schedule.time_per_entry_seconds) }}
                      </span>
                    </div>
                    @if (hasMultipleTracks()) {
                      <div class="track-selector">
                        <select class="track-dropdown"
                                [ngModel]="schedule.track_id"
                                (ngModelChange)="updateScheduleTrack(schedule, $event)">
                          <option [ngValue]="null">{{ 'manageEvent.schedule.noTrack' | translate }}</option>
                          @for (track of tracks; track track.track_id) {
                            <option [ngValue]="track.track_id">{{ track.track_name }}</option>
                          }
                        </select>
                      </div>
                    }
                    <span class="status-pill" [class]="schedule.status === 'live' ? 'draft' : schedule.status">
                      {{ schedule.status === 'live' ? 'draft' : schedule.status }}
                    </span>
                    <div class="schedule-actions">
                      <!-- TEMPORARILY DISABLED FOR V1
                      @if (schedule.status === 'draft' || schedule.status === 'published') {
                        <button class="icon-btn live" (click)="startLiveSchedule(schedule, item.eventClass!)" [title]="'manageEvent.schedule.goLive' | translate">
                          <i class="fa fa-play"></i>
                        </button>
                      }
                      @if (schedule.status === 'live') {
                        <button class="icon-btn live active" (click)="openLiveSession(schedule)" [title]="'liveSession.manageLive' | translate">
                          <i class="fa fa-broadcast-tower"></i>
                        </button>
                      }
                      -->
                      <button class="icon-btn" (click)="openScheduleEditor(schedule, item.eventClass!)" [title]="'common.edit' | translate">
                        <i class="fa fa-pen"></i>
                      </button>
                      <button class="icon-btn" (click)="openCopyScheduleModal(schedule, item.eventClass!)" [title]="'common.copy' | translate">
                        <i class="fa fa-copy"></i>
                      </button>
                      <button class="icon-btn danger" (click)="deleteSchedule(schedule, item.eventClass!)" [title]="'common.delete' | translate">
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div class="no-schedules">
                <i class="fa fa-stream"></i>
                <span>{{ 'manageEvent.schedule.noSchedulesYet' | translate }}</span>
              </div>
            }

            <!-- Add Break Button (show after each class except if there's already a break) -->
            @if (!isLast && !hasBreakAfterClass(item.eventClass.event_class_id)) {
              <div class="add-break-section">
                @if (showBreakForm === item.eventClass.event_class_id) {
                  <div class="break-form">
                    <div class="break-form-row">
                      <select [(ngModel)]="newBreakDuration" class="break-duration-select">
                        <option [ngValue]="15">15 {{ 'manageEvent.schedule.min' | translate }}</option>
                        <option [ngValue]="30">30 {{ 'manageEvent.schedule.min' | translate }}</option>
                        <option [ngValue]="45">45 {{ 'manageEvent.schedule.min' | translate }}</option>
                        <option [ngValue]="60">1 {{ 'manageEvent.schedule.hour' | translate }}</option>
                        <option [ngValue]="90">1.5 {{ 'manageEvent.schedule.hours' | translate }}</option>
                        <option [ngValue]="120">2 {{ 'manageEvent.schedule.hours' | translate }}</option>
                      </select>
                      <input type="text" [(ngModel)]="newBreakNotes" [placeholder]="'manageEvent.schedule.breakNotesPlaceholder' | translate" class="break-notes-input" />
                      <button class="btn-add-break" (click)="addDayBreak(item.eventClass!.event_class_id)">
                        <i class="fa fa-plus"></i> {{ 'manageEvent.schedule.add' | translate }}
                      </button>
                      <button class="btn-cancel-break" (click)="showBreakForm = null">
                        <i class="fa fa-times"></i>
                      </button>
                    </div>
                  </div>
                } @else {
                  <button class="add-break-btn" (click)="toggleBreakForm(item.eventClass!.event_class_id)">
                    <i class="fa fa-coffee"></i>
                    {{ 'manageEvent.schedule.addBreak' | translate }}
                  </button>
                }
              </div>
            }
          </div>
        } @else if (item.type === 'break' && item.break) {
          <!-- Day Break Display -->
          <div class="day-break-section">
            <div class="day-break-indicator">
              <div class="break-line"></div>
              <div class="break-content">
                <i class="fa fa-coffee"></i>
                <span class="break-text">{{ item.break.notes }}</span>
                <span class="break-duration">{{ formatBreakDuration(item.break.duration_minutes) }}</span>
                <button class="remove-break-btn" (click)="removeDayBreak(item.break!)" [title]="'manageEvent.schedule.removeBreak' | translate">
                  <i class="fa fa-times"></i>
                </button>
              </div>
              <div class="break-line"></div>
            </div>
          </div>
        }
      }
    </div>
  }
</div>

@if (showTypeSelector !== null) {
  <div class="backdrop" (click)="showTypeSelector = null"></div>
}
